import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Disc3, Check, X, Search, Loader2, Eye, Clock, 
  CheckCircle, XCircle, AlertCircle, Star, User,
  MapPin, Phone, Mail, Music, ChevronDown, Edit2, Ban, Trash2, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const StatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock, text: 'Pendiente' },
    approved: { color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle, text: 'Aprobado' },
    rejected: { color: 'bg-red-500/20 text-red-400', icon: XCircle, text: 'Rechazado' },
    suspended: { color: 'bg-orange-500/20 text-orange-400', icon: AlertCircle, text: 'Suspendido' }
  };
  const { color, icon: Icon, text } = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon size={12} /> {text}
    </span>
  );
};

const DJApplicationsPage = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDJ, setEditingDJ] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    artistic_name: '',
    real_name: '',
    email: '',
    phone: '',
    bio: '',
    genres: [],
    city: '',
    province: '',
    photo_url: '',
    banner_url: '',
    status: '',
    featured: false,
    spotify_url: '',
    soundcloud_url: '',
    mixcloud_url: ''
  });

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/dj/admin/applications${params}`);
      setApplications(response.data.applications);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (djId) => {
    setSubmitting(true);
    try {
      await api.post(`/dj/admin/applications/${djId}/approve`);
      toast.success('DJ aprobado correctamente');
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (rejectReason.length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/dj/admin/applications/${selectedApp.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Solicitud rechazada');
      fetchApplications();
      setSelectedApp(null);
      setRejectDialogOpen(false);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleFeatured = async (djId, currentFeatured) => {
    try {
      await api.put(`/dj/admin/applications/${djId}/featured?featured=${!currentFeatured}`);
      toast.success(currentFeatured ? 'DJ quitado de destacados' : 'DJ marcado como destacado');
      fetchApplications();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const openEditModal = (dj) => {
    setEditingDJ(dj);
    setEditFormData({
      artistic_name: dj.artistic_name || '',
      real_name: dj.real_name || '',
      email: dj.email || dj.user_email || '',
      phone: dj.phone || '',
      bio: dj.bio || '',
      genres: dj.genres || [],
      city: dj.city || '',
      province: dj.province || '',
      photo_url: dj.photo_url || '',
      banner_url: dj.banner_url || '',
      status: dj.status || 'pending',
      featured: dj.featured || false,
      spotify_url: dj.spotify_url || dj.social_links?.spotify || '',
      soundcloud_url: dj.soundcloud_url || dj.social_links?.soundcloud || '',
      mixcloud_url: dj.mixcloud_url || dj.social_links?.mixcloud || ''
    });
    setShowEditModal(true);
  };

  const handleSaveDJ = async () => {
    if (!editingDJ) return;
    
    setSaving(true);
    try {
      await api.put(`/dj/admin/${editingDJ.id}`, editFormData);
      toast.success('DJ actualizado correctamente');
      setShowEditModal(false);
      setEditingDJ(null);
      fetchApplications();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar DJ');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspendDJ = async (djId, currentStatus) => {
    const newStatus = currentStatus === 'suspended' ? 'approved' : 'suspended';
    const action = currentStatus === 'suspended' ? 'reactivar' : 'suspender';
    
    if (!window.confirm(`¿Estás seguro de que querés ${action} este DJ?`)) {
      return;
    }
    
    try {
      await api.put(`/dj/admin/${djId}`, { status: newStatus });
      toast.success(currentStatus === 'suspended' ? 'DJ reactivado correctamente' : 'DJ suspendido correctamente');
      fetchApplications();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || `Error al ${action} DJ`);
    }
  };

  const handleDeleteDJ = async (djId, djName) => {
    if (!window.confirm(`¿Estás seguro de que querés eliminar permanentemente a "${djName}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await api.delete(`/dj/admin/${djId}`);
      toast.success('DJ eliminado correctamente');
      fetchApplications();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar DJ');
    }
  };

  const filteredApps = applications.filter(app => 
    app.artistic_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.real_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check for DJ-related permissions
  const hasDjAccess = hasPermission('dj_requests_view') || hasPermission('dj_requests_approve') || hasPermission('dj_requests_manage');

  if (!hasDjAccess) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
              <h3 className="text-xl font-bold mb-2">Acceso Restringido</h3>
              <p className="text-zinc-400">No tenés permiso para gestionar solicitudes de DJ</p>
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center">
                <Disc3 size={28} className="text-black" />
              </div>
              <div>
                <h1 className="text-2xl font-black">Solicitudes DJ</h1>
                <p className="text-zinc-500 text-sm">Revisá y aprobá cuentas de DJ</p>
              </div>
            </div>
            <Button
              onClick={async () => {
                try {
                  const res = await api.post('/dj/admin/migrate-slugs');
                  const data = res.data;
                  if (data.migrated_count > 0) {
                    toast.success(`Se migraron ${data.migrated_count} slugs de DJs`);
                    fetchApplications();
                  } else {
                    toast.info('Todos los DJs ya tienen slug');
                  }
                } catch (err) {
                  toast.error('Error al migrar slugs');
                }
              }}
              variant="outline"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="migrate-dj-slugs-btn"
            >
              Migrar Slugs DJ
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setStatusFilter('pending')}
              className={`p-4 rounded-xl border transition-all ${
                statusFilter === 'pending' ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-[#121212] border-[#27272A]'
              }`}
            >
              <p className="text-3xl font-black text-yellow-400">{stats.pending || 0}</p>
              <p className="text-sm text-zinc-500">Pendientes</p>
            </button>
            <button
              onClick={() => setStatusFilter('approved')}
              className={`p-4 rounded-xl border transition-all ${
                statusFilter === 'approved' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#121212] border-[#27272A]'
              }`}
            >
              <p className="text-3xl font-black text-emerald-400">{stats.approved || 0}</p>
              <p className="text-sm text-zinc-500">Aprobados</p>
            </button>
            <button
              onClick={() => setStatusFilter('suspended')}
              className={`p-4 rounded-xl border transition-all ${
                statusFilter === 'suspended' ? 'bg-orange-500/10 border-orange-500/30' : 'bg-[#121212] border-[#27272A]'
              }`}
            >
              <p className="text-3xl font-black text-orange-400">{stats.suspended || 0}</p>
              <p className="text-sm text-zinc-500">Suspendidos</p>
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={`p-4 rounded-xl border transition-all ${
                statusFilter === 'rejected' ? 'bg-red-500/10 border-red-500/30' : 'bg-[#121212] border-[#27272A]'
              }`}
            >
              <p className="text-3xl font-black text-red-400">{stats.rejected || 0}</p>
              <p className="text-sm text-zinc-500">Rechazados</p>
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre, email..."
              className="pl-9 bg-[#121212] border-[#27272A]"
            />
          </div>

          {/* Applications List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={40} className="animate-spin text-[#D4AF37]" />
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="text-center py-20 bg-[#121212] border border-[#27272A] rounded-2xl">
              <Disc3 size={48} className="mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-bold mb-2">Sin solicitudes</h3>
              <p className="text-zinc-500">No hay solicitudes {statusFilter && `con estado "${statusFilter}"`}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredApps.map(app => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#121212] border border-[#27272A] rounded-xl p-5 hover:border-[#3F3F46] transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        {app.photo_url ? (
                          <img src={app.photo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                        ) : (
                          <Disc3 size={24} className="text-[#D4AF37]" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-lg">{app.artistic_name}</h3>
                          <StatusBadge status={app.status} />
                          {app.applicant_type === 'agency' && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded text-xs font-bold uppercase" data-testid={`agency-badge-${app.id}`}>
                              Agencia
                            </span>
                          )}
                          {app.managed_by && (
                            <span className="px-2 py-0.5 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded text-xs" title="DJ gestionado por una agencia">
                              Roster: {app.managed_by}
                            </span>
                          )}
                          {app.featured && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded text-xs">
                              <Star size={10} /> Destacado
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400">{app.real_name}</p>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Mail size={12} /> {app.user_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={12} /> {app.city}
                          </span>
                          {app.applicant_type === 'agency' && app.website && (
                            <a href={app.website.startsWith('http') ? app.website : `https://${app.website}`} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">
                              {app.website}
                            </a>
                          )}
                          {app.applicant_type === 'agency' && app.roster_size && (
                            <span>Roster: {app.roster_size} DJs</span>
                          )}
                          {app.applicant_type === 'agency' && app.contact_role && (
                            <span>Cargo: {app.contact_role}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {(app.genres || []).slice(0, 4).map(g => (
                            <span key={g} className="px-2 py-0.5 bg-[#27272A] rounded text-xs">{g}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSelectedApp(app)} className="border-[#27272A]">
                        <Eye size={14} className="mr-1" /> Ver
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openEditModal(app)} 
                        className="border-[#27272A] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                        title="Editar DJ"
                      >
                        <Edit2 size={14} />
                      </Button>
                      {app.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => handleApprove(app.id)} className="bg-emerald-500 hover:bg-emerald-600">
                            <Check size={14} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedApp(app); setRejectDialogOpen(true); }}
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                            <X size={14} />
                          </Button>
                        </>
                      )}
                      {app.status === 'approved' && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleToggleFeatured(app.id, app.featured)}
                            className={app.featured ? 'border-[#D4AF37]/30 text-[#D4AF37]' : 'border-[#27272A]'}
                            title={app.featured ? 'Quitar de destacados' : 'Marcar como destacado'}
                          >
                            <Star size={14} />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleSuspendDJ(app.id, app.status)}
                            className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                            title="Suspender DJ"
                          >
                            <Ban size={14} />
                          </Button>
                        </>
                      )}
                      {app.status === 'suspended' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleSuspendDJ(app.id, app.status)}
                          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                          title="Reactivar DJ"
                        >
                          <RotateCcw size={14} />
                        </Button>
                      )}
                      {app.status === 'rejected' && (
                        <Button
                          size="sm"
                          onClick={() => handleApprove(app.id)}
                          disabled={submitting}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          title="Aprobar de todas formas"
                          data-testid={`approve-rejected-${app.id}`}
                        >
                          <Check size={14} />
                        </Button>
                      )}
                      {(app.status === 'rejected' || app.status === 'suspended') && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDeleteDJ(app.id, app.artistic_name)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          title="Eliminar permanentemente"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Detail Modal */}
          <Dialog open={!!selectedApp && !rejectDialogOpen} onOpenChange={() => setSelectedApp(null)}>
            <DialogContent className="bg-[#0A0A0A] border-[#27272A] max-w-2xl max-h-[90vh] overflow-y-auto">
              {selectedApp && (
                <>
                  <DialogHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        {selectedApp.photo_url ? (
                          <img src={selectedApp.photo_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                        ) : (
                          <Disc3 size={32} className="text-[#D4AF37]" />
                        )}
                      </div>
                      <div>
                        <DialogTitle className="text-xl">{selectedApp.artistic_name}</DialogTitle>
                        <p className="text-zinc-400">{selectedApp.real_name}</p>
                        <StatusBadge status={selectedApp.status} />
                      </div>
                    </div>
                  </DialogHeader>
                  
                  <div className="space-y-5 mt-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 p-3 bg-[#121212] rounded-lg">
                        <Mail size={14} className="text-[#D4AF37]" />
                        <span>{selectedApp.user_email}</span>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-[#121212] rounded-lg">
                        <Phone size={14} className="text-[#D4AF37]" />
                        <span>{selectedApp.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-[#121212] rounded-lg">
                        <MapPin size={14} className="text-[#D4AF37]" />
                        <span>{selectedApp.city}</span>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-[#121212] rounded-lg">
                        <Eye size={14} className="text-[#D4AF37]" />
                        <span>{selectedApp.profile_views || 0} vistas</span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold mb-2 flex items-center gap-2">
                        <Music size={16} className="text-[#D4AF37]" /> Géneros
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {(selectedApp.genres || []).map(g => (
                          <span key={g} className="px-3 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm">{g}</span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold mb-2">Biografía</h4>
                      <p className="text-zinc-400 whitespace-pre-wrap p-4 bg-[#121212] rounded-lg">{selectedApp.bio}</p>
                    </div>

                    {selectedApp.rejection_reason && (
                      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <h4 className="font-bold text-red-400 mb-2">Motivo de rechazo</h4>
                        <p className="text-zinc-400">{selectedApp.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  {selectedApp.status === 'pending' && (
                    <DialogFooter className="mt-6">
                      <Button variant="outline" onClick={() => { setRejectDialogOpen(true); }} className="border-red-500/30 text-red-400">
                        <X size={16} className="mr-2" /> Rechazar
                      </Button>
                      <Button onClick={() => handleApprove(selectedApp.id)} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600">
                        {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                        Aprobar {selectedApp.applicant_type === 'agency' ? 'Agencia' : 'DJ'}
                      </Button>
                    </DialogFooter>
                  )}
                  {selectedApp.status === 'rejected' && (
                    <DialogFooter className="mt-6">
                      <Button onClick={() => handleApprove(selectedApp.id)} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600" data-testid="approve-rejected-modal-btn">
                        {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                        Aprobar de todas formas
                      </Button>
                    </DialogFooter>
                  )}
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Reject Dialog */}
          <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
            <DialogContent className="bg-[#0A0A0A] border-[#27272A]">
              <DialogHeader>
                <DialogTitle>Rechazar solicitud</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-zinc-400">
                  ¿Estás seguro de rechazar la solicitud de <strong>{selectedApp?.artistic_name}</strong>?
                </p>
                <div>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Motivo del rechazo (mínimo 10 caracteres)..."
                    rows={4}
                    className="bg-[#121212] border-[#27272A] resize-none"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setRejectDialogOpen(false); setRejectReason(''); }}>
                  Cancelar
                </Button>
                <Button onClick={handleReject} disabled={submitting} className="bg-red-500 hover:bg-red-600">
                  {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <X size={16} className="mr-2" />}
                  Rechazar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit DJ Modal */}
          <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Edit2 className="text-[#D4AF37]" size={20} />
                  Editar DJ
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider">Información Básica</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">Nombre Artístico *</Label>
                      <Input
                        value={editFormData.artistic_name}
                        onChange={(e) => setEditFormData({...editFormData, artistic_name: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Nombre Real</Label>
                      <Input
                        value={editFormData.real_name}
                        onChange={(e) => setEditFormData({...editFormData, real_name: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Email</Label>
                      <Input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Teléfono</Label>
                      <Input
                        value={editFormData.phone}
                        onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Ciudad</Label>
                      <Input
                        value={editFormData.city}
                        onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Provincia</Label>
                      <Input
                        value={editFormData.province}
                        onChange={(e) => setEditFormData({...editFormData, province: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Biografía</Label>
                    <Textarea
                      value={editFormData.bio}
                      onChange={(e) => setEditFormData({...editFormData, bio: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[80px]"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Géneros (separados por coma)</Label>
                    <Input
                      value={editFormData.genres.join(', ')}
                      onChange={(e) => setEditFormData({...editFormData, genres: e.target.value.split(',').map(g => g.trim()).filter(Boolean)})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="House, Techno, Electronic"
                    />
                  </div>
                </div>

                {/* Status & Featured */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider">Estado</h4>
                  <div className="flex gap-6">
                    <div>
                      <Label className="text-zinc-400">Estado de la solicitud</Label>
                      <select
                        value={editFormData.status}
                        onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                        className="w-full mt-1 bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                      >
                        <option value="pending">Pendiente</option>
                        <option value="approved">Aprobado</option>
                        <option value="rejected">Rechazado</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer self-end pb-2">
                      <input
                        type="checkbox"
                        checked={editFormData.featured}
                        onChange={(e) => setEditFormData({...editFormData, featured: e.target.checked})}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                      <span className="text-zinc-300 text-sm flex items-center gap-1">
                        <Star size={14} className="text-[#D4AF37]" /> Destacado
                      </span>
                    </label>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider">Imágenes</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">URL Foto de Perfil</Label>
                      <Input
                        value={editFormData.photo_url}
                        onChange={(e) => setEditFormData({...editFormData, photo_url: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">URL Banner</Label>
                      <Input
                        value={editFormData.banner_url}
                        onChange={(e) => setEditFormData({...editFormData, banner_url: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider">Redes Sociales</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label className="text-zinc-400">Spotify URL</Label>
                      <Input
                        value={editFormData.spotify_url}
                        onChange={(e) => setEditFormData({...editFormData, spotify_url: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        placeholder="https://open.spotify.com/artist/..."
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">SoundCloud URL</Label>
                      <Input
                        value={editFormData.soundcloud_url}
                        onChange={(e) => setEditFormData({...editFormData, soundcloud_url: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        placeholder="https://soundcloud.com/..."
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Mixcloud URL</Label>
                      <Input
                        value={editFormData.mixcloud_url}
                        onChange={(e) => setEditFormData({...editFormData, mixcloud_url: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        placeholder="https://mixcloud.com/..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-zinc-800">
                <Button
                  variant="ghost"
                  onClick={() => setShowEditModal(false)}
                  className="text-zinc-400"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveDJ}
                  disabled={saving || !editFormData.artistic_name}
                  className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Guardar Cambios
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default DJApplicationsPage;
